import React from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Button, Image, Select, Space, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, PictureOutlined } from "@ant-design/icons";

import { ItemSchema, ItemSchemaGroup, MenuType } from "../../types/model";
import { formatCents } from "./pricing";
import { ComboAddonTier } from "./types";

const { Text } = Typography;

export const MENU_TYPES: ReadonlyArray<MenuType> = ["dine_in", "large_volume"];

export function menuTypeLabel(t: TFunction, menuType: MenuType | string): string {
  switch (menuType) {
    case "dine_in":
      return t("dine_in_pickup");
    case "large_volume":
      return t("group_order");
    default:
      return menuType;
  }
}

/** A tier's label, falling back to its price ("$2.00 tier"). */
export function tierLabel(t: TFunction, tier: Pick<ComboAddonTier, "name" | "price">): string {
  const name = (tier.name ?? "").trim();
  return name !== "" ? name : t("combo_tier_default_name", { price: formatCents(tier.price) });
}

export const itemImageUrl = (item: ItemSchema): string | undefined =>
  item.image_manifest?.default.url ?? (item.image_url || undefined);

type ItemThumbProps = {
  item?: ItemSchema;
  size?: number;
};

export const ItemThumb: React.FC<ItemThumbProps> = ({ item, size = 32 }) => {
  const url = item ? itemImageUrl(item) : undefined;
  const style: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 4,
    flex: "none",
  };
  if (!url) {
    return (
      <div
        style={{
          ...style,
          background: "rgba(0,0,0,0.04)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#bfbfbf",
        }}
      >
        <PictureOutlined />
      </div>
    );
  }
  return (
    <Image
      src={url}
      alt={item?.name}
      width={size}
      height={size}
      preview={false}
      style={{ ...style, objectFit: "cover" }}
    />
  );
};

type ItemPickerProps = {
  // Sections to pick from (already restricted to one menu type).
  groups: ReadonlyArray<ItemSchemaGroup>;
  // Currently selected item schema IDs, in order.
  value: ReadonlyArray<string>;
  // New selection. Newly picked items are appended in pick order; removed
  // ones are dropped, keeping the order of the rest.
  onChange: (ids: string[]) => void;
  placeholder?: string;
  testId?: string;
};

/**
 * Searchable multi-select over the item schemas of one menu type, grouped by
 * menu section. Each option shows the thumbnail, name and regular price. The
 * selection itself is rendered by the caller (as an ordered list), so the
 * select only shows a count.
 */
export const ItemPicker: React.FC<ItemPickerProps> = ({
  groups,
  value,
  onChange,
  placeholder,
  testId,
}) => {
  const { t } = useTranslation();

  const options = groups
    .filter((g) => (g.item_schemas ?? []).length > 0)
    .map((g) => ({
      label: g.name,
      title: g.name,
      options: [...(g.item_schemas ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((item) => ({
          value: item.id,
          searchText: `${item.name} ${item.pos_key ?? ""} ${g.name}`.toLowerCase(),
          label: (
            <Space size={8} style={{ display: "flex" }}>
              <ItemThumb item={item} size={24} />
              <Text>{item.name}</Text>
              <Text type="secondary">{formatCents(item.price)}</Text>
            </Space>
          ),
        })),
    }));

  const handleChange = (next: string[]) => {
    const nextSet = new Set(next);
    const kept = value.filter((id) => nextSet.has(id));
    const keptSet = new Set(kept);
    onChange([...kept, ...next.filter((id) => !keptSet.has(id))]);
  };

  return (
    <Select<string[]>
      mode="multiple"
      data-testid={testId}
      style={{ width: "100%" }}
      value={[...value]}
      onChange={handleChange}
      options={options}
      placeholder={placeholder ?? t("combo_search_items")}
      showSearch
      filterOption={(input, option) =>
        String((option as { searchText?: string } | undefined)?.searchText ?? "").includes(
          input.trim().toLowerCase()
        )
      }
      maxTagCount={0}
      maxTagPlaceholder={() => t("combo_items_selected", { count: value.length })}
      notFoundContent={t("combo_no_items_found")}
      listHeight={320}
    />
  );
};

type MoveButtonsProps = {
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  size?: "small" | "middle";
};

export const MoveButtons: React.FC<MoveButtonsProps> = ({
  index,
  count,
  onMove,
  size = "small",
}) => {
  const { t } = useTranslation();
  return (
    <Space size={0}>
      <Button
        type="text"
        size={size}
        aria-label={t("move_up")}
        icon={<ArrowUpOutlined />}
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      />
      <Button
        type="text"
        size={size}
        aria-label={t("move_down")}
        icon={<ArrowDownOutlined />}
        disabled={index >= count - 1}
        onClick={() => onMove(index, index + 1)}
      />
    </Space>
  );
};
